import { type HTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";

import { AnalyticsDataSource, type DataHubMetrics, type ExtendedMetric, Metric } from "@doitintl/cmp-models";
import { MoreVert } from "@mui/icons-material";
import {
  Autocomplete,
  type AutocompleteRenderGroupParams,
  type AutocompleteRenderInputParams,
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  type SxProps,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import isEqual from "lodash/isEqual";

import { metricFiltersText, reportText } from "../../../assets/texts";
import { entitlementsTxt } from "../../../assets/texts/CloudAnalytics/entitlements";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { useDrawerStore } from "../../../Pages/CloudAnalytics/report/ReportQuickView/hooks/useDrawerStore";
import { createFilterOptions, CSPMetric, getBaseMetricOptions } from "../../../Pages/CloudAnalytics/utilities";
import { type MetricWSnap } from "../../../types";
import { usePrevious } from "../../../utils/usePrevious";
import { useCustomMetrics } from "../../hooks/cloudAnalytics/useCustomMetrics";

type MetricOption = {
  label: string;
  groupName: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
};

const emptyMetricOption: MetricOption = {
  label: "",
  groupName: "",
  value: "",
};

export type Props = {
  dataCy?: string;
  dataSource: AnalyticsDataSource;
  datahubMetrics?: DataHubMetrics[];
  disabled?: boolean;
  disabledTooltip?: string;
  extendedMetrics: ExtendedMetric[];
  forcePopupIcon?: boolean;
  handleMetricSelect: (metricVal: Metric, value: MetricWSnap | string) => void;
  handleResetLimits?: () => void;
  inputLabel?: string;
  isCSP?: boolean;
  selectedCalculatedMetric: MetricWSnap | null;
  selectedExtendedMetric?: string | null;
  selectedMetric: number | null;
  showCustomMetrics?: boolean;
  size?: "medium" | "small";
  sx?: SxProps<Theme>;
};

const MetricSelect = ({
  dataCy = "metric-select",
  dataSource,
  datahubMetrics,
  disabled,
  disabledTooltip,
  extendedMetrics,
  forcePopupIcon = true,
  handleMetricSelect,
  handleResetLimits = () => {},
  inputLabel = metricFiltersText.METRIC_VALUE_LABEL,
  isCSP,
  selectedCalculatedMetric,
  selectedExtendedMetric,
  selectedMetric,
  showCustomMetrics = true,
  size = "small",
  sx,
}: Props) => {
  const [metrics, metricsLoading] = useCustomMetrics();
  const isEntitledAmortizedCostSavings = useIsFeatureEntitled("analytics:extendedMetrics:amortizedCostSavings");

  const isDataSourceBilling = dataSource === AnalyticsDataSource.BILLING;
  const isDataSourceBillingDatahub = dataSource === AnalyticsDataSource.BILLING_DATAHUB;
  const selectedCalculatedMetricId = selectedCalculatedMetric?.ref.id;

  const metricOptionsFiltered = useMemo(() => getBaseMetricOptions(dataSource), [dataSource]);

  const extendedMetricsFiltered = useMemo(
    () =>
      extendedMetrics?.filter((m) => {
        if (isDataSourceBillingDatahub) {
          return m.dataSource === AnalyticsDataSource.BILLING_DATAHUB || m.dataSource === AnalyticsDataSource.BILLING;
        }
        return m.dataSource === dataSource;
      }),
    [dataSource, extendedMetrics, isDataSourceBillingDatahub]
  );

  const isExtendedMetricDisabled = useCallback(
    (m: ExtendedMetric) => {
      let result = { disabled: false, tooltip: "" };

      switch (m.key) {
        case "amortized_cost":
        case "amortized_savings":
          if (!isEntitledAmortizedCostSavings) {
            result = {
              disabled: true,
              tooltip: entitlementsTxt.EXTENDED_METRIC_AMORTIZED_COST_SAVINGS,
            };
          }

          break;
        default:
          break;
      }

      return result;
    },
    [isEntitledAmortizedCostSavings]
  );

  const metricOptions = useMemo(() => {
    const basicMetricsOptions: MetricOption[] = metricOptionsFiltered.map((metric) => ({
      label: metric.label,
      groupName: reportText.BASIC_METRICS,
      value: metric.value.toString(),
      disabled: false,
    }));

    if (isCSP && (isDataSourceBilling || isDataSourceBillingDatahub)) {
      basicMetricsOptions.push({
        label: CSPMetric.label,
        groupName: reportText.BASIC_METRICS,
        value: CSPMetric.value.toString(),
        disabled: false,
      });
    }

    const extendedMetricsOptions: MetricOption[] = extendedMetricsFiltered.map((metric) => {
      const { disabled, tooltip } = isExtendedMetricDisabled(metric);
      return {
        label: metric.label,
        groupName: reportText.EXTENDED_METRICS,
        value: metric.key,
        disabled,
        tooltip,
      };
    });

    let dataHubMetricsOptions: MetricOption[] = [];
    if (isDataSourceBillingDatahub && datahubMetrics && datahubMetrics.length > 0) {
      dataHubMetricsOptions = datahubMetrics
        .map((metric) => ({
          label: metric.label,
          groupName: reportText.DATAHUB_METRICS,
          value: metric.key,
          disabled: false,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    let calculatedMetricsOptions: MetricOption[] = [];
    if (showCustomMetrics && dataSource !== AnalyticsDataSource.KUBERNETES_UTILIZATION) {
      calculatedMetricsOptions = metrics
        .map((metric) => ({
          label: metric.data.name,
          groupName: reportText.CALCULATED_METRICS,
          value: metric.ref.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return [...basicMetricsOptions, ...extendedMetricsOptions, ...dataHubMetricsOptions, ...calculatedMetricsOptions];
  }, [
    metrics,
    dataSource,
    metricOptionsFiltered,
    isCSP,
    isDataSourceBilling,
    isDataSourceBillingDatahub,
    extendedMetricsFiltered,
    datahubMetrics,
    showCustomMetrics,
    isExtendedMetricDisabled,
  ]);

  const selectedMetricOption = useMemo(() => {
    let selectedMetricValue = selectedMetric?.toString();

    if (!metricsLoading && selectedCalculatedMetricId) {
      selectedMetricValue = selectedCalculatedMetricId;
    } else if (selectedExtendedMetric) {
      selectedMetricValue = selectedExtendedMetric;
    }

    if (!selectedMetricValue) {
      return emptyMetricOption;
    }

    return metricOptions.find((m) => m.value === selectedMetricValue) ?? emptyMetricOption;
  }, [metricOptions, selectedCalculatedMetricId, selectedExtendedMetric, selectedMetric, metricsLoading]);

  const prevSelected = usePrevious(selectedMetricOption);

  // todo check if this is needed
  useEffect(() => {
    if (!prevSelected) {
      return;
    }
    if (parseInt(prevSelected.value) <= Metric.MARGIN) {
      return;
    }

    if (!isEqual(selectedMetricOption, prevSelected)) {
      handleResetLimits();
    }
  }, [prevSelected, selectedMetricOption, handleResetLimits]);

  const filterOptions = createFilterOptions<MetricOption>({
    trim: true,
    ignoreAccents: true,
    ignoreCase: true,
    matchFrom: "any",
  });

  const handleChange = useCallback(
    (_, metricOption: MetricOption | null) => {
      if (!metricOption) {
        return;
      }

      switch (metricOption.groupName) {
        case reportText.BASIC_METRICS:
          handleMetricSelect(Number(metricOption?.value) as Metric, "");
          break;
        case reportText.CALCULATED_METRICS: {
          const cMetric = metrics?.find((m) => m.ref.id === metricOption.value);
          if (cMetric) {
            handleMetricSelect(Metric.CALCULATED, cMetric);
          }
          break;
        }
        case reportText.EXTENDED_METRICS:
        case reportText.DATAHUB_METRICS:
          handleMetricSelect(Metric.EXTENDED, metricOption.value);
          break;
        default:
          break;
      }
    },
    [handleMetricSelect, metrics]
  );

  const isError =
    (selectedMetric === Metric.EXTENDED || selectedMetric === Metric.CALCULATED) &&
    !selectedCalculatedMetricId &&
    !selectedExtendedMetric;
  const { openMetric } = useDrawerStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const onQuickViewClick = useCallback(() => {
    const metric = metrics?.find((m) => m.ref.id === selectedMetricOption.value);
    if (metric) {
      openMetric(metric);
    }
    setAnchorEl(null);
  }, [metrics, openMetric, selectedMetricOption.value]);

  const handleRenderInput = (params: AutocompleteRenderInputParams) => (
    <Tooltip title={disabled ? disabledTooltip : ""}>
      <TextField
        {...params}
        label={inputLabel}
        size={size}
        data-cy="metric-select-input"
        margin="dense"
        error={isError}
        helperText={isError ? reportText.NO_ACCESS_METRIC : ""}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
          input: {
            ...params.InputProps,
            ...(selectedMetricOption.groupName === reportText.CALCULATED_METRICS && {
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                      }}
                      edge="end"
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => {
                        setAnchorEl(null);
                      }}
                      anchorOrigin={{
                        vertical: "center",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "center",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem onClick={onQuickViewClick}>Quick view</MenuItem>
                    </Menu>
                  </InputAdornment>
                </>
              ),
            }),
          },
        }}
      />
    </Tooltip>
  );

  const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: MetricOption) => (
    <Tooltip title={option.tooltip ?? ""} key={option.value}>
      <span>
        <li {...props} style={{ fontSize: "14px" }}>
          {option.label}
        </li>
      </span>
    </Tooltip>
  );

  const handleRenderGroup = (params: AutocompleteRenderGroupParams) => (
    <Box key={params.key}>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.disabled",
          position: "sticky",
          top: "-8px",
          padding: "5px 10px",
          backgroundColor: "background.paper",
        }}
      >
        {params.group}
      </Typography>
      {params.children}
    </Box>
  );

  return (
    <Autocomplete
      data-cy={dataCy}
      disableClearable
      disabled={disabled}
      filterOptions={filterOptions}
      forcePopupIcon={forcePopupIcon}
      getOptionDisabled={(option) => option.disabled ?? false}
      groupBy={(option) => option.groupName}
      onChange={handleChange}
      options={metricOptions}
      renderGroup={handleRenderGroup}
      renderInput={handleRenderInput}
      renderOption={handleRenderOption}
      selectOnFocus={false}
      size={size}
      sx={sx}
      value={selectedMetricOption}
      loading={metricsLoading}
    />
  );
};

export default MetricSelect;
